/**
 * Pre/Post Conditions Step Component
 * 
 * Third step in the MedPass workflow - defines monitoring requirements,
 * vital signs, and assessment conditions before and after medication administration.
 */

import React, { useState, useEffect } from 'react';
import {
    Card,
    Row,
    Col,
    Form,
    Input,
    Select,
    Button,
    Alert,
    Space,
    Table,
    Modal,
    message,
    Tabs,
    InputNumber,
    Checkbox,
    Tag
} from 'antd';
import {
    HeartOutlined,
    PlusOutlined,
    DeleteOutlined,
    EditOutlined,
    ExclamationCircleOutlined
} from '@ant-design/icons';

import medpassService from '../../../services/medpassService';

const { Option } = Select;
const { TextArea } = Input;
const { TabPane } = Tabs;

const PrePostConditionsStep = ({ workflow, onSave, onNext, onPrev, loading }) => {
    const [form] = Form.useForm();
    const [preConditions, setPreConditions] = useState([]);
    const [postConditions, setPostConditions] = useState([]);
    const [vitalSignsRequired, setVitalSignsRequired] = useState([]);
    const [showConditionModal, setShowConditionModal] = useState(false);
    const [editingCondition, setEditingCondition] = useState(null);
    const [conditionType, setConditionType] = useState('pre');
    const [templates, setTemplates] = useState({});

    // Vital sign types with their normal ranges
    const vitalSignTypes = {
        'blood_pressure': { 
            label: 'Blood Pressure', 
            unit: 'mmHg',
            normalRange: 'Systolic: 90-140, Diastolic: 60-90'
        },
        'heart_rate': { 
            label: 'Heart Rate', 
            unit: 'bpm',
            normalRange: '60-100 bpm'
        },
        'temperature': { 
            label: 'Temperature', 
            unit: '°F',
            normalRange: '97.0-99.5°F'
        },
        'respiratory_rate': { 
            label: 'Respiratory Rate', 
            unit: '/min',
            normalRange: '12-20 per minute'
        },
        'oxygen_saturation': { 
            label: 'Oxygen Saturation', 
            unit: '%',
            normalRange: '95-100%'
        },
        'blood_glucose': { 
            label: 'Blood Glucose', 
            unit: 'mg/dL',
            normalRange: '70-140 mg/dL'
        },
        'weight': { 
            label: 'Weight', 
            unit: 'kg',
            normalRange: 'Patient-specific'
        },
        'height': { 
            label: 'Height', 
            unit: 'cm',
            normalRange: 'Patient-specific'
        },
        'pain_scale': { 
            label: 'Pain Scale', 
            unit: '0-10',
            normalRange: '0-3 (mild)'
        }
    };

    // Initialize with existing data
    useEffect(() => {
        if (workflow) {
            setPreConditions(workflow.pre_conditions || []);
            setPostConditions(workflow.post_conditions || []);
            setVitalSignsRequired(workflow.vital_signs_required || []);
        }
    }, [workflow]);

    // Load templates
    useEffect(() => {
        loadConditionTemplates();
    }, []);

    const loadConditionTemplates = async () => {
        try {
            const result = await medpassService.getConditionTemplates('pre');
            if (result.success) {
                setTemplates(result.data);
            }
        } catch (error) {
            console.error('Error loading templates:', error);
        }
    };

    const addCondition = (type) => {
        setConditionType(type);
        setEditingCondition(null);
        form.resetFields();
        setShowConditionModal(true);
    };

    const editCondition = (condition, type, index) => {
        setConditionType(type);
        setEditingCondition({ ...condition, index, type });
        
        form.setFieldsValue({
            description: condition.description,
            required_vitals: condition.required_vitals || [],
            assessment_questions: condition.assessment_questions || [],
            patient_instructions: condition.patient_instructions,
            caregiver_instructions: condition.caregiver_instructions,
            monitoring_frequency: condition.monitoring_frequency
        });
        
        setShowConditionModal(true);
    };

    const saveCondition = async () => {
        try {
            const values = await form.validateFields();
            
            const newCondition = {
                condition_id: editingCondition?.condition_id || `cond_${Date.now()}`,
                condition_type: conditionType,
                description: values.description,
                required_vitals: values.required_vitals || [],
                vital_ranges: {},
                assessment_questions: values.assessment_questions || [],
                patient_instructions: values.patient_instructions,
                caregiver_instructions: values.caregiver_instructions,
                monitoring_frequency: values.monitoring_frequency,
                alert_thresholds: {}
            };

            if (editingCondition) {
                // Update existing condition
                if (conditionType === 'pre') {
                    const updated = [...preConditions];
                    updated[editingCondition.index] = newCondition;
                    setPreConditions(updated);
                } else {
                    const updated = [...postConditions];
                    updated[editingCondition.index] = newCondition;
                    setPostConditions(updated);
                }
            } else {
                // Add new condition
                if (conditionType === 'pre') {
                    setPreConditions([...preConditions, newCondition]);
                } else {
                    setPostConditions([...postConditions, newCondition]);
                }
            }

            setShowConditionModal(false);
            form.resetFields();
            message.success('Condition saved successfully');
        } catch (error) {
            message.error('Please complete all required fields');
        }
    };

    const removeCondition = (index, type) => {
        if (type === 'pre') {
            setPreConditions(preConditions.filter((_, i) => i !== index));
        } else {
            setPostConditions(postConditions.filter((_, i) => i !== index));
        }
        message.success('Condition removed');
    };

    const applyTemplate = (templateData) => {
        form.setFieldsValue({
            description: templateData.description,
            required_vitals: templateData.required_vitals || [],
            assessment_questions: templateData.assessment_questions || [],
            patient_instructions: templateData.patient_instructions,
            caregiver_instructions: templateData.caregiver_instructions,
            monitoring_frequency: templateData.monitoring_frequency
        });
        message.success('Template applied');
    };

    // Table columns for conditions
    const conditionColumns = [
        {
            title: 'Description',
            dataIndex: 'description',
            key: 'description',
            ellipsis: true
        },
        {
            title: 'Required Vitals',
            dataIndex: 'required_vitals',
            key: 'required_vitals',
            render: (vitals) => (
                <div>
                    {vitals?.map(vital => (
                        <Tag key={vital} size="small">
                            {vitalSignTypes[vital]?.label || vital}
                        </Tag>
                    ))}
                </div>
            )
        },
        {
            title: 'Monitoring',
            dataIndex: 'monitoring_frequency',
            key: 'monitoring_frequency'
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record, index) => (
                <Space>
                    <Button 
                        type="text" 
                        icon={<EditOutlined />}
                        onClick={() => editCondition(record, conditionType, index)}
                        size="small"
                    >
                        Edit
                    </Button>
                    <Button 
                        type="text" 
                        danger 
                        icon={<DeleteOutlined />}
                        onClick={() => removeCondition(index, conditionType)}
                        size="small"
                    >
                        Remove
                    </Button>
                </Space>
            )
        }
    ];

    // Save step data
    const handleSave = async () => {
        const updateData = {
            pre_conditions: preConditions,
            post_conditions: postConditions,
            vital_signs_required: vitalSignsRequired
        };

        const success = await onSave(updateData, 'conditions');
        if (success) {
            message.success('Pre/Post conditions saved successfully');
            return true;
        }
        return false;
    };

    const handleNext = async () => {
        const success = await handleSave();
        if (success && onNext) {
            // Add a small delay to ensure workflow state is updated
            await new Promise(resolve => setTimeout(resolve, 100));
            await onNext();
        }
    };

    return (
        <div className="pre-post-conditions-step">
            <Card 
                title={
                    <Space>
                        <HeartOutlined />
                        Pre/Post Conditions & Monitoring
                    </Space>
                }
            >
                <Tabs defaultActiveKey="pre" type="card">
                    <TabPane 
                        tab={`Pre-Conditions (${preConditions.length})`} 
                        key="pre"
                    >
                        <div style={{ marginBottom: 16 }}>
                            <Alert
                                message="Pre-Administration Conditions"
                                description="Define vital signs, assessments, and conditions that must be checked BEFORE giving medications."
                                type="info"
                                showIcon
                                style={{ marginBottom: 16 }}
                            />
                            
                            <Button 
                                type="primary" 
                                icon={<PlusOutlined />}
                                onClick={() => addCondition('pre')}
                            >
                                Add Pre-Condition
                            </Button>
                        </div>

                        <Table
                            dataSource={preConditions}
                            columns={conditionColumns}
                            rowKey="condition_id"
                            pagination={false}
                            locale={{
                                emptyText: (
                                    <div style={{ padding: '20px', textAlign: 'center' }}>
                                        <HeartOutlined style={{ fontSize: '24px', color: '#ccc' }} />
                                        <div style={{ marginTop: 8, color: '#999' }}>
                                            No pre-conditions defined
                                        </div>
                                    </div>
                                )
                            }}
                        />
                    </TabPane>

                    <TabPane 
                        tab={`Post-Conditions (${postConditions.length})`} 
                        key="post"
                    >
                        <div style={{ marginBottom: 16 }}>
                            <Alert
                                message="Post-Administration Conditions"
                                description="Define monitoring requirements and assessments AFTER medication administration."
                                type="info"
                                showIcon
                                style={{ marginBottom: 16 }}
                            />
                            
                            <Button 
                                type="primary" 
                                icon={<PlusOutlined />}
                                onClick={() => addCondition('post')}
                            >
                                Add Post-Condition
                            </Button>
                        </div>

                        <Table
                            dataSource={postConditions}
                            columns={conditionColumns}
                            rowKey="condition_id"
                            pagination={false}
                            locale={{
                                emptyText: (
                                    <div style={{ padding: '20px', textAlign: 'center' }}>
                                        <HeartOutlined style={{ fontSize: '24px', color: '#ccc' }} />
                                        <div style={{ marginTop: 8, color: '#999' }}>
                                            No post-conditions defined
                                        </div>
                                    </div>
                                )
                            }}
                        />
                    </TabPane>

                    <TabPane tab="Required Vital Signs" key="vitals">
                        <Alert
                            message="Required Vital Signs"
                            description="Select which vital signs must be monitored for this medication regimen."
                            type="info"
                            showIcon
                            style={{ marginBottom: 16 }}
                        />

                        <Checkbox.Group
                            value={vitalSignsRequired}
                            onChange={setVitalSignsRequired}
                            style={{ width: '100%' }}
                        >
                            <Row gutter={[16, 16]}>
                                {Object.entries(vitalSignTypes).map(([key, config]) => (
                                    <Col xs={24} sm={12} md={8} key={key}>
                                        <Card size="small" style={{ height: '100%' }}>
                                            <Checkbox value={key} style={{ marginBottom: 8 }}>
                                                <strong>{config.label}</strong>
                                            </Checkbox>
                                            <div style={{ fontSize: '12px', color: '#666' }}>
                                                Unit: {config.unit}
                                            </div>
                                            <div style={{ fontSize: '12px', color: '#666' }}>
                                                Normal: {config.normalRange}
                                            </div>
                                        </Card>
                                    </Col>
                                ))}
                            </Row>
                        </Checkbox.Group>
                    </TabPane>
                </Tabs>

                {/* Action Buttons */}
                <Row justify="space-between" style={{ marginTop: 24 }}>
                    <Col>
                        <Button onClick={onPrev}>
                            Previous
                        </Button>
                    </Col>
                    
                    <Col>
                        <Button type="primary" onClick={handleNext} loading={loading}>
                            Save & Next Step
                        </Button>
                    </Col>
                </Row>
            </Card>

            {/* Condition Modal */}
            <Modal
                title={`${editingCondition ? 'Edit' : 'Add'} ${conditionType === 'pre' ? 'Pre' : 'Post'}-Condition`}
                open={showConditionModal}
                onOk={saveCondition}
                onCancel={() => {
                    setShowConditionModal(false);
                    form.resetFields();
                }}
                width={800}
            >
                <Form
                    form={form}
                    layout="vertical"
                >
                    <Form.Item
                        label="Condition Description"
                        name="description"
                        rules={[{ required: true, message: 'Description is required' }]}
                    >
                        <TextArea 
                            placeholder="Describe what needs to be checked or monitored"
                            rows={3}
                        />
                    </Form.Item>

                    <Form.Item
                        label="Required Vital Signs"
                        name="required_vitals"
                    >
                        <Select 
                            mode="multiple"
                            placeholder="Select vital signs to monitor"
                            style={{ width: '100%' }}
                        >
                            {Object.entries(vitalSignTypes).map(([key, config]) => (
                                <Option key={key} value={key}>
                                    {config.label} ({config.unit})
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        label="Assessment Questions"
                        name="assessment_questions"
                    >
                        <Select
                            mode="tags"
                            placeholder="Enter assessment questions (press Enter to add)"
                            style={{ width: '100%' }}
                        />
                    </Form.Item>

                    <Row gutter={16}>
                        <Col xs={24} sm={12}>
                            <Form.Item
                                label="Patient Instructions"
                                name="patient_instructions"
                            >
                                <TextArea 
                                    placeholder="Instructions for the patient"
                                    rows={3}
                                />
                            </Form.Item>
                        </Col>
                        
                        <Col xs={24} sm={12}>
                            <Form.Item
                                label="Caregiver Instructions"
                                name="caregiver_instructions"
                            >
                                <TextArea 
                                    placeholder="Instructions for caregivers/nurses"
                                    rows={3}
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item
                        label="Monitoring Frequency"
                        name="monitoring_frequency"
                    >
                        <Select placeholder="How often should this be monitored?">
                            <Option value="before_each_dose">Before each dose</Option>
                            <Option value="daily">Daily</Option>
                            <Option value="twice_daily">Twice daily</Option>
                            <Option value="weekly">Weekly</Option>
                            <Option value="as_needed">As needed</Option>
                            <Option value="30_minutes_after">30 minutes after dose</Option>
                            <Option value="1_hour_after">1 hour after dose</Option>
                            <Option value="2_hours_after">2 hours after dose</Option>
                        </Select>
                    </Form.Item>
                </Form>

                {/* Template suggestions */}
                {Object.keys(templates).length > 0 && (
                    <div style={{ marginTop: 16 }}>
                        <h4>Quick Templates:</h4>
                        <Space wrap>
                            {Object.entries(templates).map(([category, templateList]) => (
                                templateList.map((template, index) => (
                                    <Button 
                                        key={`${category}-${index}`}
                                        size="small" 
                                        type="dashed"
                                        onClick={() => applyTemplate(template)}
                                    >
                                        {category}: {template.description.substring(0, 30)}...
                                    </Button>
                                ))
                            ))}
                        </Space>
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default PrePostConditionsStep;